import React, { FC, useCallback } from "react"
import { ReactComponent as InfoIcon } from "icons/lib/info.svg"
import { Title, Checkbox, ExclusiveChoiceDropdown, Tooltip } from "dash-styled"
import { DownloadType } from "../../../../../../Hooks/useJobListDownload"
import { ReactComponent as TriangleIcon } from "icons/lib/triangle.svg"
import { DownloadSettingsProps } from "./DownloadTypeModal"
import CustomFileNames from "./CustomFileNames"

const fieldSeparatorChoices = [
    {
        value: ",",
        label: "[ , ] Comma"
    },
    {
        value: ";",
        label: "[ ; ] Semicolon"
    },
    {
        value: "|",
        label: "[ | ] Pipe"
    }
]

const quoteValuesChoices = [
    {
        value: true,
        label: "Always"
    },
    {
        value: false,
        label: "Never"
    }
]

interface AdvancedSettingsProps {
    toggleExpanded?: () => void
    isExpanded: boolean
    type: DownloadType
    handleSetTypeFlatCSV: () => void
    downloadSettings?: DownloadSettingsProps
}
const AdvancedSettings: FC<AdvancedSettingsProps> = ({
    toggleExpanded,
    isExpanded,
    type,
    handleSetTypeFlatCSV,
    downloadSettings
}) => {
    const isCSV = type === "csv" || type === "flat_csv"

    const handleCsvQuoteValueClicked = useCallback(
        (index: number) => {
            const newValue = quoteValuesChoices[index].value
            if (
                newValue !== downloadSettings?.appDownloadSettings.quoteValues
            ) {
                downloadSettings?.onDownloadSettingsChanged({
                    ...downloadSettings.appDownloadSettings,
                    quoteValues: newValue
                })
            }
        },
        [downloadSettings]
    )

    const handleFieldDelimiterClicked = useCallback(
        (index: number) => {
            const newValue = fieldSeparatorChoices[index].value
            if (
                newValue !==
                downloadSettings?.appDownloadSettings.fieldDelimiter
            ) {
                downloadSettings?.onDownloadSettingsChanged({
                    ...downloadSettings.appDownloadSettings,
                    fieldDelimiter: newValue
                })
            }
        },
        [downloadSettings]
    )

    const handleIncludeAnnotationChange = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            includeAnnotations:
                !downloadSettings.appDownloadSettings.includeAnnotations
        })
    }, [downloadSettings])

    const handleFlattenTablesChange = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            flattenTables: !downloadSettings.appDownloadSettings.flattenTables
        })
    }, [downloadSettings])

    const handleExcludeHeaderRowChange = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            excludeHeaderRow:
                !downloadSettings.appDownloadSettings.excludeHeaderRow
        })
    }, [downloadSettings])

    const handleToggleFeedback = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            includeMessages:
                !downloadSettings.appDownloadSettings.includeMessages
        })
    }, [downloadSettings])

    const handleSaveSettingsChange = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            saveSettings: !downloadSettings.appDownloadSettings.saveSettings
        })
    }, [downloadSettings])

    return (
        <>
            <button
                onClick={toggleExpanded}
                className="w-full px-1 mt-5"
                data-testid="download-advanced-settings"
            >
                <Title
                    component="h3"
                    variant="md"
                    className="flex items-baseline m-0 text-rhino-900"
                >
                    <TriangleIcon
                        className={`transition duration-200 ease-in-out mr-2 transform ${
                            isExpanded ? "rotate-90" : ""
                        }`}
                    />
                    Advanced settings
                </Title>
            </button>
            {isExpanded && (
                <div>
                    {type !== "json" && (
                        <Checkbox
                            variant="modern"
                            onChange={handleSetTypeFlatCSV}
                            isChecked={
                                !downloadSettings?.appDownloadSettings
                                    .expandOutputColumns ?? type === "csv"
                            }
                            className="ml-5 text-rhino-900"
                            testId="download-csv-checkbox"
                        >
                            Don’t expand output columns
                        </Checkbox>
                    )}
                    <Checkbox
                        variant="modern"
                        onChange={handleToggleFeedback}
                        isChecked={
                            downloadSettings?.appDownloadSettings
                                .includeMessages
                        }
                        className="ml-5 text-rhino-900"
                        testId="download-feedback-checkbox"
                    >
                        Include messages
                    </Checkbox>
                    {downloadSettings?.isIncludeAnnotationsEnabled && (
                        <div className="flex flex-row">
                            <Checkbox
                                variant="modern"
                                onChange={handleIncludeAnnotationChange}
                                isChecked={
                                    downloadSettings.appDownloadSettings
                                        .includeAnnotations
                                }
                                className="ml-5 text-rhino-900"
                                testId="download-include-annotations-checkbox"
                            >
                                Include annotations
                            </Checkbox>

                            <Tooltip title="Annotations are e.g. bounding boxes, segments, or similar.">
                                <InfoIcon style={{ color: "#5B5B5B" }} />
                            </Tooltip>
                        </div>
                    )}
                    {downloadSettings?.isFlattenTablesEnabled && (
                        <div className="flex flex-row">
                            <Checkbox
                                variant="modern"
                                onChange={handleFlattenTablesChange}
                                isChecked={
                                    downloadSettings.appDownloadSettings
                                        .flattenTables
                                }
                                className="ml-5 text-rhino-900"
                                testId="download-flatten-tables-checkbox"
                            >
                                Flatten tables
                            </Checkbox>

                            <Tooltip title="In case a table was extracted each line item of the table will be a separate line and key value pairs are repeated for each line.">
                                <InfoIcon style={{ color: "#5B5B5B" }} />
                            </Tooltip>
                        </div>
                    )}
                    {isCSV && (
                        <Checkbox
                            variant="modern"
                            onChange={handleExcludeHeaderRowChange}
                            isChecked={
                                downloadSettings?.appDownloadSettings
                                    .excludeHeaderRow
                            }
                            className="ml-5 text-rhino-900"
                            testId="download-exclude-header-row-checkbox"
                        >
                            Don’t include header row
                        </Checkbox>
                    )}
                    {isCSV && (
                        <div className="ml-5 mt-5">
                            <div className="flex flex-row pb-2">
                                <Title
                                    component="h3"
                                    variant="sm"
                                    className="flex items-baseline m-0 text-rhino-900"
                                >
                                    Format
                                </Title>
                            </div>
                            <div className="flex flex-row w-full ml-2 pb-2">
                                <div className="flex flex-col w-1/3">
                                    <span>Field separator</span>
                                </div>
                                <div className="flex flex-col w-full">
                                    <ExclusiveChoiceDropdown
                                        choices={fieldSeparatorChoices.map(
                                            c => c.label
                                        )}
                                        selectedIndex={fieldSeparatorChoices.findIndex(
                                            c =>
                                                c.value ===
                                                downloadSettings
                                                    ?.appDownloadSettings
                                                    .fieldDelimiter
                                        )}
                                        className="w-1/3 ml-3"
                                        inputClassName="h-6"
                                        testId="download-csv-format-separator-dropdown"
                                        fitContentWidth={true}
                                        size="sm"
                                        onClick={handleFieldDelimiterClicked}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-row w-full ml-2">
                                <div className="flex flex-col w-1/3">
                                    <span>Quote value</span>
                                </div>
                                <div className="flex flex-col w-full">
                                    <ExclusiveChoiceDropdown
                                        choices={quoteValuesChoices.map(
                                            c => c.label
                                        )}
                                        selectedIndex={quoteValuesChoices.findIndex(
                                            c =>
                                                c.value ===
                                                downloadSettings
                                                    ?.appDownloadSettings
                                                    .quoteValues
                                        )}
                                        className="w-1/3 ml-3"
                                        inputClassName="h-6"
                                        testId="download-csv-format-quote-dropdown"
                                        fitContentWidth={true}
                                        size="sm"
                                        onClick={handleCsvQuoteValueClicked}
                                    />
                                </div>
                            </div>
                            {downloadSettings?.isCustomFileNamesEnabled && (
                                <CustomFileNames
                                    downloadSettings={downloadSettings}
                                />
                            )}
                        </div>
                    )}

                    {downloadSettings && (
                        <Checkbox
                            variant="modern"
                            onChange={handleSaveSettingsChange}
                            isChecked={
                                downloadSettings.appDownloadSettings
                                    .saveSettings
                            }
                            className="ml-5 text-rhino-900"
                            testId="download-save-settings-checkbox"
                        >
                            Remember download settings
                        </Checkbox>
                    )}
                </div>
            )}
        </>
    )
}

export default AdvancedSettings
